import { useNavigate, Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { useCart } from "@/lib/cart";
import { useAuth } from "@/lib/auth";
import { useCurrency } from "@/lib/currency";
import { useLang } from "@/lib/i18n";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import { Trash2, Plus, Minus, CreditCard, Smartphone, Landmark, Tag, X } from "lucide-react";
import { toast } from "sonner";

export default function Cart() {
  const { items, setQty, remove, total, clear } = useCart();
  const { user } = useAuth();
  const { format, convertFromEUR, currency, country } = useCurrency();
  const { t, lang } = useLang();
  const nav = useNavigate();
  const [delivery, setDelivery] = useState("pickup");
  const [address, setAddress] = useState("");
  const [payMethod, setPayMethod] = useState("pawapay");  // pawapay | bank_transfer (Flutterwave hidden en attente compte marchand carte)
  // PawaPay v2 supports both USD and CDF for Vodacom/Airtel/Orange RDC.
  // USD is the default because most DRC diaspora + urban customers keep
  // their mobile-money wallet balance in USD.
  const [pawapayCurrency, setPawapayCurrency] = useState("USD");
  const [loading, setLoading] = useState(false);
  const [couponCode, setCouponCode] = useState("");
  const [coupon, setCoupon] = useState(null); // {code, discount_eur, discount_type, discount_value}
  const [couponLoading, setCouponLoading] = useState(false);
  const [shippingOptions, setShippingOptions] = useState([]);
  const [shippingSlug, setShippingSlug] = useState("pickup");
  const [termsAccepted, setTermsAccepted] = useState(false);

  useEffect(() => {
    api.get("/shipping/options").then((r) => setShippingOptions(r.data || [])).catch(() => {});
  }, []);

  const shipping = shippingOptions.find((o) => o.slug === shippingSlug) || { fee_eur: 0 };
  const shippingFee = shipping.fee_eur || 0;
  const discount = coupon?.discount_eur || 0;
  const finalTotal = Math.max(0, total - discount) + shippingFee;

  const applyCoupon = async () => {
    if (!couponCode.trim()) return;
    setCouponLoading(true);
    try {
      const { data } = await api.post("/coupons/validate", {
        code: couponCode.trim(),
        subtotal_eur: total,
      });
      setCoupon(data);
      toast.success(`Coupon ${data.code} appliqué : -${data.discount_eur.toFixed(2)} €`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Coupon invalide");
      setCoupon(null);
    } finally { setCouponLoading(false); }
  };

  const removeCoupon = () => {
    setCoupon(null);
    setCouponCode("");
  };

  const checkout = async () => {
    if (!user) { toast.error("Connectez-vous pour finaliser"); return nav("/connexion"); }
    if (items.length === 0) return;
    if (!termsAccepted) { toast.error(t("cart.terms.error")); return; }

    setLoading(true);
    try {
      if (payMethod === "bank_transfer") {
        const { data } = await api.post("/orders/bank-transfer", {
          items: items.map((i) => ({ kind: i.kind, item_id: i.item_id, quantity: i.quantity })),
          delivery, address,
          coupon_code: coupon?.code || null,
          shipping_option: shippingSlug,
        });
        clear();
        toast.success(`Commande créée. Référence : ${data.reference}`);
        nav(`/virement/${data.order_id}`);
      } else if (payMethod === "flutterwave") {
        const { data } = await api.post("/flutterwave/checkout", {
          items: items.map((i) => ({ kind: i.kind, item_id: i.item_id, quantity: i.quantity })),
          origin_url: window.location.origin,
          delivery, address, country,
          coupon_code: coupon?.code || null,
          shipping_option: shippingSlug,
        });
        window.location.href = data.checkout_url;
      } else if (payMethod === "stripe") {
        const { data } = await api.post("/checkout/create", {
          items: items.map((i) => ({ kind: i.kind, item_id: i.item_id, quantity: i.quantity })),
          origin_url: window.location.origin,
          delivery, address, country,
          payment_method: "card",
          provider: "stripe",
          coupon_code: coupon?.code || null,
          shipping_option: shippingSlug,
        });
        window.location.href = data.checkout_url;
      } else {
        const { data } = await api.post("/pawapay/checkout", {
          items: items.map((i) => ({ kind: i.kind, item_id: i.item_id, quantity: i.quantity })),
          origin_url: window.location.origin,
          delivery, address, country,
          coupon_code: coupon?.code || null,
          shipping_option: shippingSlug,
          currency: country === "CD" ? pawapayCurrency : undefined,
        });
        window.location.href = data.checkout_url;
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur au paiement");
      setLoading(false);
    }
  };

  return (
    <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-12" data-testid="cart-page">
      <h1 className="heading font-extrabold text-4xl mb-8">{t("cart.title")}</h1>

      {items.length === 0 ? (
        <div className="text-center py-16" data-testid="empty-cart">
          <p className="text-[#4B5563] mb-4">{t("cart.empty")}</p>
          <Link to="/produits"><Button className="pill-btn bg-[#1A3322] rounded-full">{t("common.explore")}</Button></Link>
        </div>
      ) : (
        <div className="grid lg:grid-cols-[1fr_400px] gap-10">
          <div className="space-y-4" data-testid="cart-items">
            {items.map((it) => (
              <div key={`${it.kind}-${it.item_id}`} className="flex items-center gap-4 border border-[#1A3322]/10 rounded-lg p-4 bg-white" data-testid={`cart-item-${it.item_id}`}>
                {it.image && <img src={it.image} alt={it.title} className="w-20 h-20 object-cover rounded"/>}
                <div className="flex-1">
                  <p className="font-semibold text-[#111812]">{it.title}</p>
                  <p className="text-xs uppercase tracking-wider text-[#4B5563] mt-1">{it.kind}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Button size="icon" variant="outline" onClick={() => setQty(it.item_id, it.kind, it.quantity - 1)} data-testid={`qty-minus-${it.item_id}`}><Minus className="w-4 h-4"/></Button>
                  <span className="w-8 text-center font-semibold" data-testid={`qty-${it.item_id}`}>{it.quantity}</span>
                  <Button size="icon" variant="outline" onClick={() => setQty(it.item_id, it.kind, it.quantity + 1)} data-testid={`qty-plus-${it.item_id}`}><Plus className="w-4 h-4"/></Button>
                </div>
                <p className="font-bold text-lg w-32 text-right">{format(convertFromEUR(it.price * it.quantity))}</p>
                <Button size="icon" variant="ghost" onClick={() => remove(it.item_id, it.kind)} data-testid={`remove-${it.item_id}`}><Trash2 className="w-4 h-4 text-[#C85A32]"/></Button>
              </div>
            ))}
          </div>

          <div className="bg-white border border-[#1A3322]/10 rounded-lg p-6 space-y-5 h-fit sticky top-24" data-testid="cart-summary">
            <p className="label-uppercase text-xs text-[#1A3322]">{t("cart.summary")}</p>

            <div className="flex justify-between text-sm text-[#4B5563]">
              <span>{t("common.subtotal")}</span>
              <span data-testid="cart-subtotal">{format(convertFromEUR(total))}</span>
            </div>

            {shippingFee > 0 && (
              <div className="flex justify-between text-sm text-[#4B5563]" data-testid="cart-shipping-row">
                <span>{t("cart.shipping_line")}</span>
                <span data-testid="cart-shipping-fee">+ {format(convertFromEUR(shippingFee))}</span>
              </div>
            )}

            {coupon && (
              <div className="flex justify-between items-center text-sm bg-[#F5F5F0] rounded-lg px-3 py-2" data-testid="cart-discount-row">
                <div className="flex items-center gap-2">
                  <Tag className="w-4 h-4 text-[#1A3322]" />
                  <span className="font-semibold text-[#1A3322]">{coupon.code}</span>
                  <span className="text-xs text-[#4B5563]">
                    {coupon.discount_type === "percentage" ? `-${coupon.discount_value}%` : `-${coupon.discount_value} €`}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-[#C85A32]" data-testid="cart-discount-amount">-{format(convertFromEUR(discount))}</span>
                  <button onClick={removeCoupon} className="text-[#4B5563] hover:text-[#C85A32]" data-testid="remove-coupon-btn"><X className="w-3.5 h-3.5"/></button>
                </div>
              </div>
            )}

            <div className="flex justify-between text-lg pt-3 border-t border-[#1A3322]/10">
              <span>{t("common.total")}</span>
              <span className="heading font-extrabold text-2xl text-[#1A3322]" data-testid="cart-total">{format(convertFromEUR(finalTotal))}</span>
            </div>

            {!coupon && (
              <div className="pt-3 border-t border-[#1A3322]/10" data-testid="coupon-section">
                <Label className="flex items-center gap-1.5 mb-2"><Tag className="w-3.5 h-3.5"/>{t("cart.coupon.label")}</Label>
                <div className="flex gap-2">
                  <Input
                    placeholder={t("cart.coupon.placeholder")}
                    value={couponCode}
                    onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                    className="uppercase"
                    data-testid="coupon-input"
                  />
                  <Button onClick={applyCoupon} disabled={couponLoading || !couponCode.trim()} variant="outline" className="rounded-full" data-testid="coupon-apply-btn">
                    {couponLoading ? "…" : t("common.apply")}
                  </Button>
                </div>
              </div>
            )}

            <p className="text-xs text-[#4B5563]">Le paiement sera prélevé en EUR ({finalTotal.toFixed(2)} €). Devise d'affichage : {currency}.</p>

            <div className="space-y-3 pt-4 border-t border-[#1A3322]/10" data-testid="payment-info">
              <Label>{t("cart.payment")}</Label>

              <RadioGroup value={payMethod} onValueChange={setPayMethod} className="space-y-3">
                <label className={`flex flex-col gap-3 border-2 rounded-lg p-4 cursor-pointer transition ${payMethod === "pawapay" ? "border-[#1A3322] bg-white" : "border-[#1A3322]/15 bg-white"}`}>
                  <div className="flex items-center gap-2">
                    <RadioGroupItem value="pawapay" data-testid="pay-pawapay"/>
                    <Smartphone className="w-5 h-5 text-[#1A3322]"/>
                    <p className="font-semibold text-[#111812]">{t("cart.pay.mobile")}</p>
                    <span className="text-[10px] bg-[#1A3322] text-white px-2 py-0.5 rounded-full font-bold ml-auto">PawaPay</span>
                  </div>
                  <div className="grid grid-cols-3 gap-2 items-center pl-6">
                    <img src="https://static-content.pawapay.io/provider_logos/vodacom.png" alt="Vodacom M-Pesa" className="h-8 object-contain"/>
                    <img src="https://static-content.pawapay.io/provider_logos/airtel.png" alt="Airtel Money" className="h-8 object-contain"/>
                    <img src="https://static-content.pawapay.io/provider_logos/orange.png" alt="Orange Money" className="h-8 object-contain"/>
                  </div>
                  {country === "CD" && payMethod === "pawapay" && (
                    <div className="pl-6 space-y-2 border-t border-[#1A3322]/10 pt-3" data-testid="pawapay-currency-block">
                      <p className="text-[11px] uppercase tracking-wider font-bold text-[#1A3322]">💳 {t("cart.pay.wallet_currency")}</p>
                      <div className="flex gap-2">
                        {["USD", "CDF"].map((cur) => {
                          const rate = cur === "USD" ? 1.08 : 2900;
                          const converted = (finalTotal * rate).toLocaleString(lang === "en" ? "en-GB" : "fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                          return (
                            <button
                              type="button"
                              key={cur}
                              onClick={(e) => { e.preventDefault(); setPawapayCurrency(cur); }}
                              className={`flex-1 border-2 rounded-lg p-2 text-left transition ${pawapayCurrency === cur ? "border-[#C85A32] bg-[#FFF7ED]" : "border-[#1A3322]/15 bg-white hover:border-[#1A3322]/40"}`}
                              data-testid={`pawapay-currency-${cur.toLowerCase()}`}
                            >
                              <p className="text-sm font-bold text-[#111812]">
                                {cur === "USD" ? "💵 USD" : "🇨🇩 CDF"}
                              </p>
                              <p className="text-[11px] text-[#4B5563] font-mono">≈ {converted} {cur}</p>
                            </button>
                          );
                        })}
                      </div>
                      <p className="text-[11px] text-[#4B5563] leading-snug">
                        {t("cart.pay.wallet_currency_hint")}
                      </p>
                    </div>
                  )}
                  <p className="text-[11px] text-[#4B5563] pl-6">Vodacom · Airtel · Orange · {t("cart.pay.debit_in")} {country === "CD" ? pawapayCurrency : currency}</p>
                </label>

                <label className={`flex flex-col gap-2 border-2 rounded-lg p-4 cursor-pointer transition hidden ${payMethod === "flutterwave" ? "border-[#1A3322] bg-white" : "border-[#1A3322]/15 bg-white"}`}>
                  <div className="flex items-center gap-2">
                    <RadioGroupItem value="flutterwave" data-testid="pay-flutterwave"/>
                    <CreditCard className="w-5 h-5 text-[#1A3322]"/>
                    <p className="font-semibold text-[#111812]">Carte bancaire</p>
                    <span className="text-[10px] bg-[#F5A623] text-white px-2 py-0.5 rounded-full font-bold ml-auto">Flutterwave</span>
                  </div>
                  <p className="text-xs text-[#4B5563] pl-7">Visa · Mastercard · Amex — paiement instantané et sécurisé en {country === "CD" ? "USD" : currency}.</p>
                </label>

                <label className={`flex flex-col gap-2 border-2 rounded-lg p-4 cursor-pointer transition hidden ${payMethod === "stripe" ? "border-[#1A3322] bg-white" : "border-[#1A3322]/15 bg-white"}`}>
                  <div className="flex items-center gap-2">
                    <RadioGroupItem value="stripe" data-testid="pay-stripe"/>
                    <CreditCard className="w-5 h-5 text-[#1A3322]"/>
                    <p className="font-semibold text-[#111812]">Carte bancaire</p>
                    <span className="text-[10px] bg-[#635BFF] text-white px-2 py-0.5 rounded-full font-bold ml-auto">Stripe</span>
                  </div>
                  <p className="text-xs text-[#4B5563] pl-7">Visa · Mastercard · Amex — paiement instantané et sécurisé.</p>
                </label>

                <label className={`flex flex-col gap-2 border-2 rounded-lg p-4 cursor-pointer transition ${payMethod === "bank_transfer" ? "border-[#1A3322] bg-white" : "border-[#1A3322]/15 bg-white"}`}>
                  <div className="flex items-center gap-2">
                    <RadioGroupItem value="bank_transfer" data-testid="pay-bank"/>
                    <Landmark className="w-5 h-5 text-[#1A3322]"/>
                    <p className="font-semibold text-[#111812]">{t("cart.pay.bank")}</p>
                    <span className="text-[10px] bg-[#D98A2C] text-white px-2 py-0.5 rounded-full font-bold ml-auto">{t("cart.pay.bank.badge")}</span>
                  </div>
                  <p className="text-xs text-[#4B5563] pl-7">Rawbank · Equity BCDC · TMB — idéal pour les commandes &gt; 500 USD. Validation manuelle du vendeur sous 24h.</p>
                </label>
              </RadioGroup>

              <div className="flex items-center gap-2 pt-2 text-[11px] text-[#1A3322]">
                <span>🔒</span>
                <span className="font-semibold">Paiement 100% sécurisé</span>
                <span className="text-[#4B5563]">· Chiffrement bancaire</span>
              </div>
            </div>

            <div className="space-y-3 pt-4 border-t border-[#1A3322]/10" data-testid="shipping-block">
              <Label>{t("cart.shipping_title")}</Label>
              <RadioGroup value={shippingSlug} onValueChange={(v) => { setShippingSlug(v); setDelivery(v === "pickup" ? "pickup" : "local_delivery"); }} className="space-y-2">
                {shippingOptions.map((opt) => (
                  <label key={opt.slug} className={`flex items-start gap-3 border rounded-lg p-3 cursor-pointer ${shippingSlug === opt.slug ? "border-[#1A3322] bg-[#F5F5F0]" : "border-[#1A3322]/15"}`} data-testid={`shipping-option-${opt.slug}`}>
                    <RadioGroupItem value={opt.slug} className="mt-1"/>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-semibold text-sm text-[#111812]">{lang === "en" ? opt.name_en : opt.name_fr}</span>
                        <span className={`font-bold text-sm shrink-0 ${opt.fee_eur === 0 ? "text-green-700" : "text-[#C85A32]"}`}>
                          {opt.fee_eur === 0 ? t("cart.shipping_free") : format(convertFromEUR(opt.fee_eur))}
                        </span>
                      </div>
                      <p className="text-[11px] text-[#4B5563] mt-0.5 leading-snug">{lang === "en" ? opt.description_en : opt.description_fr}</p>
                    </div>
                  </label>
                ))}
              </RadioGroup>
              {shippingSlug !== "pickup" && (
                <div className="space-y-2">
                  <Label>{t("cart.delivery.address")}</Label>
                  <Input value={address} onChange={(e) => setAddress(e.target.value)} data-testid="delivery-address" placeholder={t("cart.delivery.address_placeholder")}/>
                </div>
              )}
            </div>

            <div className="pt-4 border-t border-[#1A3322]/10" data-testid="terms-block">
              <label className="flex items-start gap-3 cursor-pointer group">
                <Checkbox
                  checked={termsAccepted}
                  onCheckedChange={(v) => setTermsAccepted(v === true)}
                  className="mt-0.5 border-[#1A3322]/40 data-[state=checked]:bg-[#1A3322] data-[state=checked]:border-[#1A3322]"
                  data-testid="terms-checkbox"
                />
                <span className="text-[13px] text-[#1F2A22] leading-snug">
                  {t("cart.terms.pre")}{" "}
                  <Link to="/cgv" target="_blank" rel="noopener noreferrer" className="text-[#1A3322] underline underline-offset-2 font-semibold hover:text-[#D98A2C]" data-testid="terms-cgv-link">
                    {t("cart.terms.link")}
                  </Link>
                  {" "}{t("cart.terms.post")}
                </span>
              </label>
            </div>

            <Button onClick={checkout} disabled={loading || !termsAccepted} className="w-full pill-btn bg-[#C85A32] hover:bg-[#D98A2C] rounded-full h-12 disabled:opacity-50 disabled:cursor-not-allowed" data-testid="checkout-btn">
              {loading ? "…" :
                payMethod === "bank_transfer" ? t("cart.checkout.bank") :
                payMethod === "flutterwave" ? "Payer par carte" :
                payMethod === "stripe" ? "Payer par carte" :
                t("cart.checkout.pawapay")}
            </Button>
            <Button variant="ghost" onClick={clear} className="w-full text-[#4B5563]" data-testid="clear-cart">{t("cart.clear")}</Button>
          </div>
        </div>
      )}
    </div>
  );
}
